import { useState, useEffect } from 'react'
import { Dispatch } from 'redux'
import * as Yup from 'yup'
import { AuthSidePanel } from './AuthSidePanel'
import { Link } from 'react-router-dom'
import {
  AuthFooter,
  decryptPassword,
  formatSecondTime,
  formattedPhoneNumber,
  handleNotificationWithToastify,
  validatePhoneField,
} from '../../../services/Functional'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../../../setup'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { CustomInput } from './custominput/CustomInput'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
// import GeoapifyGeocoderAutocompleteComponent from './autocomplete/GeoapifyAutocompleteComponent'
import PhoneInput from 'react-phone-input-2'
import './accept-invite.scss'
import { AcceptInviteDialog } from './dialogs/AcceptInviteDialog'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  getUserById,
  handleDeleteData,
  handleGenerateOTP,
  handleResendOTP,
  handleVerifyOTP,
} from '../../../../setup/redux/auth/actions'
import { Loader } from '../../../services/Loader'
import moment from 'moment'
import { CustomOtpInput } from './otpinput/CustomOtpInput'
import PasswordValidationUI, {
  checkPasswordValidity,
} from '../../../components/common/PasswordValidationUI'
import { User } from '../../users/users-list/core/_models'

const initialValues = {
  user_id: null,
  company_id: null,
  group: '',
  name: '',
  email: '',
  password: '',
  confirm_password: '',
  country_code: '',
  phone: '',
  address: null,
  groups: [],
  is_already_user: null as string | null,
  company_name: '',
}

const acceptInviteSchema = Yup.object().shape({
  password: Yup.string()
    .max(50, 'Password must not exceed 50 characters')
    .when('is_already_user', ([isAlreadyUser]: string[], schema) =>
      isAlreadyUser === 'Yes' ? schema.notRequired() : schema.required('Password is required')
    ),

  confirm_password: Yup.string()
    .oneOf([Yup.ref('password'), undefined], 'Passwords do not match')
    .when('is_already_user', ([isAlreadyUser]: string[], schema) =>
      isAlreadyUser === 'Yes'
        ? schema.notRequired()
        : schema.required('Confirm password is required')
    ),

  phone: Yup.string()
    .min(8, 'Phone must be at least 8 digits')
    .required('Phone is required')
    .test('verifyPhoneValidation', 'Phone number is not valid', function (value) {
      const { country_code } = this.parent as { country_code: string }
      return validatePhoneField(value || '', country_code)
    }),
})

export function AcceptInvite() {
  const { config, actionsLoading }: any = useSelector<RootState>(
    (state: RootState) => ({
      config: state.configuration.configurations,
      actionsLoading: state.common.actionsLoading,
    }),
    shallowEqual
  )

  const dispatch: Dispatch<any> = useDispatch()
  const location = useLocation()
  let navigate = useNavigate()
  const urlSearchParams = new URLSearchParams(location.search)
  const userId = urlSearchParams.get('user_id')
  const companyId = decryptPassword(urlSearchParams?.get('company_id'))
  const decodedId = decryptPassword(userId)
  let intervalId: NodeJS.Timeout | undefined
  const countdownDurationShort = 30
  const countdownDurationLong = 120
  const maxAttempts = 3
  const [isTouched, setIsTouched] = useState<boolean>(false)
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false)
  const [passwordShow, setPasswordShow] = useState(false)
  const [confirmPasswordShow, setConfirmPasswordShow] = useState(false)
  const [showOTPField, setShowOTPField] = useState(false)
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
  const [otp, setOTP] = useState('')
  const [resendOTPbuttonClicked, setResendOTPbuttonClicked] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [values, setValues] = useState(initialValues)
  const [isDeletedUserLink, setIsDeletedUserLink] = useState(false)
  const [remainingTime, setRemainingTime] = useState(30)
  const [resendCount, setResendCount] = useState(0)
  const [userData, setUserData] = useState<User | undefined>(undefined)

  const storedMaxResendCount = localStorage.getItem('otpMaxResendCount')
  const maxResendCount = storedMaxResendCount ? parseInt(storedMaxResendCount) : maxAttempts

  useEffect(() => {
    dispatch(handleDeleteData())
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (userId && companyId) {
          let response: any = await dispatch(getUserById(decodedId, 'show'))
          if (response.data.code === 200 || response.data.code === 201) {
            if (response.data.data && response.data.data.user) {
              setUserData(response.data.data.user)
            }
            const userCompanyRoles = response.data?.data?.user?.users_groups?.filter(
              (item: any) => item.company_id === companyId
            )
            if (
              !response.data.data.user ||
              userCompanyRoles?.every((item: any) => item.is_invite_accepted)
            ) {
              setIsDeletedUserLink(true)
            } else {
              setIsDeletedUserLink(false)
            }
          } else {
            navigate('/logout')
          }
        }
      } catch (error) {
        navigate('/logout')
      }
    }

    fetchData()

    const intervalId = setInterval(() => {
      fetchData()
    }, 30000)

    return () => {
      clearInterval(intervalId)
    }
  }, [userId, dispatch])

  useEffect(() => {
    const decodedData: any = {}
    const requiredParameters = [
      'name',
      'email',
      'group',
      'user_id',
      'is_already_user',
      'company_id',
      'company_name',
      'phone',
      'country_code',
    ]
    const urlSearchParams = new URLSearchParams(location.search)
    const groups: string[] = []

    urlSearchParams.forEach((value, key) => {
      if (key === 'user_id' || key === 'company_id') {
        decodedData[key] = decryptPassword(value)
      } else if (key.startsWith('groups')) {
        groups.push(decodeURIComponent(value))
      } else {
        decodedData[key] = decodeURIComponent(value)
      }
    })

    if (groups.length === 0) {
      navigate('/logout')
      return
    }

    decodedData.groups = groups

    const missingParameters = requiredParameters.filter((param) => !(param in decodedData))
    if (missingParameters.length > 0) {
      navigate('/logout')
      return
    }

    setValues((prevData) => ({
      ...prevData,
      ...decodedData,
      phone:
        userData && userData.users_groups?.some((item: any) => item.is_invite_accepted === true)
          ? userData.phone || ''
          : prevData.phone,
      country_code:
        userData && userData.users_groups?.some((item: any) => item.is_invite_accepted === true)
          ? userData.country_code || ''
          : prevData.country_code,
      is_already_user: 'No',
    }))
  }, [location.search])

  useEffect(() => {
    if (userData && userData.users_groups?.some((item: any) => item.is_invite_accepted === true)) {
      setIsPasswordValid(true)
      setValues((prevData) => ({
        ...prevData,
        phone: userData.phone || '',
        country_code: userData.country_code || '',
        is_already_user: 'Yes',
      }))
    }
  }, [userData])

  useEffect(() => {
    if (showOTPField) {
      if (remainingTime > 0) {
        intervalId = setInterval(() => {
          setRemainingTime((prevTime) => prevTime - 1)
        }, 1000)
      } else {
        clearInterval(intervalId!)
      }
      return () => {
        clearInterval(intervalId!)
      }
    }
  }, [remainingTime, showOTPField])

  useEffect(() => {
    localStorage.setItem('otpRemainingTime', remainingTime.toString())
    localStorage.setItem('otpResendCount', resendCount.toString())
    localStorage.setItem('otpMaxResendCount', maxResendCount.toString())
  }, [remainingTime, resendCount, maxResendCount])

  const generateOTP = async (values: any) => {
    setConfirmPasswordShow(false)
    setPasswordShow(false)
    const payload = {
      mode: 'mobile',
      mobile_no: formattedPhoneNumber(values.country_code, values.phone),
      ...(userData &&
        userData.users_groups?.every((item: any) => item.is_invite_accepted === false) && {
        user_id: values.user_id,
      }),
    }
    let response: any = await dispatch(handleGenerateOTP(payload))
    if (response.data.code === 200 || response.data.code === 201) {
      setResendOTPbuttonClicked(false)
      setShowOTPField(true)
      handleNotificationWithToastify(response.data)
      // setUserData(undefined)
    } else {
      setResendOTPbuttonClicked(false)
      setShowOTPField(false)
      handleNotificationWithToastify(response.data)
    }
  }

  const onClickResendOTP = async (values: any) => {
    const payload = {
      mode: 'mobile',
      mobile_no: formattedPhoneNumber(values.country_code, values.phone),
      user_id: values.user_id,
    }
    let response: any = await dispatch(handleResendOTP(payload))
    if (response.data.code === 200 || response.data.code === 201) {
      handleNotificationWithToastify(response.data)
      if (resendCount < maxResendCount) {
        setResendCount((prevCount) => {
          const newCount = prevCount + 1
          localStorage.setItem('otpResendCount', newCount.toString())
          localStorage.setItem('otpResendTime', moment().valueOf().toString())
          localStorage.setItem('otpRemainingTime', countdownDurationShort.toString())
          return newCount
        })
        setRemainingTime(countdownDurationShort)
        setResendOTPbuttonClicked(false)
      } else {
        setResendCount(() => {
          const newCount = 0
          localStorage.setItem('otpResendCount', newCount.toString())
          localStorage.setItem('otpResendTime', moment().valueOf().toString())
          localStorage.setItem('otpRemainingTime', countdownDurationShort.toString())
          return newCount
        })
        setRemainingTime(countdownDurationLong)
        setResendOTPbuttonClicked(false)
      }
    } else {
      setResendOTPbuttonClicked(false)
      handleNotificationWithToastify(response.data)
    }
  }

  const handleOTPChange = (otp: string) => {
    setOTP(otp)
  }

  const handleUserInvite = async (values: any) => {
    const payload = {
      id: values.user_id,
      otp: otp,
      new_mobile_no: values.phone,
      new_country_code: values.country_code,
      address: values.address,
      company_id: values.company_id,
      ...(userData &&
        userData.users_groups?.every((item: any) => item.is_invite_accepted === false) && {
        password: values.password,
      }),
    }
    let response: any = await dispatch(handleVerifyOTP(payload))
    if (response.code === 200 || response.code === 201) {
      setShowConfirmationDialog(true)
      handleNotificationWithToastify(response)
    } else {
      setHasError(true)
      handleNotificationWithToastify(response)
      setTimeout(() => {
        setHasError(false)
        setOTP('')
      }, 5000)
    }
  }

  return (
    <>
      {actionsLoading && <Loader />}
      {isDeletedUserLink ? (
        <div className='vertical_center'>
          <div className='d-flex flex-column align-items-center item-container px-6'>
            <img alt='' className='error-img mb-7' src={toAbsoluteUrl('/media/chain.svg')} />
            <h1 className='fw-bolder fs-4x text-gray-700 mb-2 text-center expired-link-text'>
              The invite link is invalid or has expired.
            </h1>
            <div className='fw-bold fs-3 text-gray-400 mb-10 text-center'>
              Please contact your Company Admin.
            </div>
          </div>
        </div>
      ) : (
        <div className='row w-100 h-100 p-0 m-0'>
          <div className='col-md-7 p-0 bg-white position-relative'>
            <div className='py-5 d-flex justify-content-between container align-items-center mb-8 position-fixed position-md-static'>
              <div className='logo-container'>
                <img src={config?.actual_logo || toAbsoluteUrl('/media/actual_logo.svg')} alt='logo' />
              </div>
              <Link
                to='https://decenthands.com/contact'
                className='link-primary fs-6 float-end'
                target='_blank'
              >
                Contact Us
              </Link>
            </div>
            <div className='accept_invite_form mx-auto'>
              <div className='h-md-100 accept_invite_form_align'>
                <div className='margin-bottom-12'>
                  <div className='accept_invite_form_header'>
                    {userData &&
                      userData.users_groups?.every((item: any) => item.is_invite_accepted === false)
                      ? 'Accept Invite'
                      : 'Invitation Accepted'}
                  </div>
                  {userData &&
                    userData.users_groups?.some(
                      (item: any) => item.is_invite_accepted === true
                    ) && (
                      <div className='accept-invite-record-updated'>Records are now updated!</div>
                    )}
                </div>
                <div>
                  <Formik
                    enableReinitialize={true}
                    initialValues={values}
                    validationSchema={acceptInviteSchema}
                    onSubmit={(values: any) => {
                      if (!resendOTPbuttonClicked) {
                        if (!showOTPField) {
                          generateOTP(values)
                        } else {
                          handleUserInvite(values)
                        }
                      }
                    }}
                  >
                    {({ values, handleSubmit, handleChange, handleBlur, setFieldValue }) => (
                      <Form className='form'>
                        <div className='row'>
                          <div className='col-md-12'>
                            <div className='form-group margin-bottom-7'>
                              <label className='accept-invite-form-label'>Company Name</label>
                              <Field
                                name='company_name'
                                className='accept-invite-form-value'
                                component={CustomInput}
                                disabled
                              />
                            </div>
                          </div>
                          <div className='col-md-12'>
                            <div className='form-group margin-bottom-7'>
                              <label className='accept-invite-form-label'>User Group</label>
                              <Field
                                name='groups'
                                className='accept-invite-form-value'
                                component={CustomInput}
                                disabled
                                value={values.groups.join(', ')}
                              />
                            </div>
                          </div>
                          <div className='col-md-12'>
                            <div className='form-group margin-bottom-7'>
                              <label className='accept-invite-form-label'>Full Name</label>
                              <Field
                                name='name'
                                placeholder='John Doe'
                                className='accept-invite-form-value'
                                component={CustomInput}
                                disabled
                              />
                            </div>
                          </div>
                          <div className='col-md-12'>
                            <div className='form-group margin-bottom-7'>
                              <label className='accept-invite-form-label'>Email</label>
                              <Field
                                name='email'
                                type='email'
                                component={CustomInput}
                                className='accept-invite-form-value'
                                placeholder='Ex. mail@website.com'
                                disabled
                              />
                            </div>
                          </div>
                          {userData &&
                            userData.users_groups?.every(
                              (item: any) => item.is_invite_accepted === false
                            ) && (
                              <>
                                <div className='col-md-12'>
                                  <div className='form-group margin-bottom-7'>
                                    <label className='accept-invite-form-label'>Password</label>
                                    <Field
                                      name='password'
                                      component={CustomInput}
                                      type={!passwordShow ? 'password' : 'text'}
                                      placeholder='******'
                                      className='accept-invite-form-value'
                                      disabled={showOTPField}
                                      onFocus={() => setIsTouched(true)}
                                      onChange={(e: any) => {
                                        handleChange(e)
                                        const newPassword = e.target.value
                                        setIsPasswordValid(checkPasswordValidity(newPassword))
                                      }}
                                      icon={
                                        <img
                                          className='cursor-pointer'
                                          alt='show'
                                          src={
                                            !passwordShow
                                              ? toAbsoluteUrl('/media/Hide.svg')
                                              : toAbsoluteUrl('/media/Show.svg')
                                          }
                                          onClick={() => setPasswordShow(!passwordShow)}
                                        />
                                      }
                                    />
                                    <ErrorMessage
                                      name='password'
                                      component='div'
                                      className='text-danger mt-2'
                                    />
                                  </div>
                                  {isTouched && (
                                    <div className='mt-7'>
                                      <PasswordValidationUI
                                        password={values.password}
                                        isTouched={isTouched}
                                      />
                                    </div>
                                  )}
                                </div>
                                {!showOTPField && (
                                  <div className='col-md-12'>
                                    <div className='form-group margin-bottom-7'>
                                      <label className='accept-invite-form-label'>
                                        Confirm Password
                                      </label>
                                      <Field
                                        name='confirm_password'
                                        component={CustomInput}
                                        type={!confirmPasswordShow ? 'password' : 'text'}
                                        placeholder='******'
                                        className='accept-invite-form-value'
                                        disabled={showOTPField}
                                        icon={
                                          <img
                                            alt='show'
                                            className='cursor-pointer'
                                            src={
                                              !confirmPasswordShow
                                                ? toAbsoluteUrl('/media/Hide.svg')
                                                : toAbsoluteUrl('/media/Show.svg')
                                            }
                                            onClick={() =>
                                              setConfirmPasswordShow(!confirmPasswordShow)
                                            }
                                          />
                                        }
                                      />
                                      <ErrorMessage
                                        name='confirm_password'
                                        component='div'
                                        className='text-danger mt-2'
                                      />
                                    </div>
                                  </div>
                                )}
                              </>
                            )}

                          {/* <div className='col-md-12'>
                                                        <div className={`form-group mb-3 accept-invite-form-address ${showOTPField && 'address-disabled'}`}>
                                                            <label className='accept-invite-form-label' htmlFor='address'>
                                                                Address
                                                            </label>
                                                            <GeoapifyGeocoderAutocompleteComponent
                                                                values={''}
                                                                placeholder='Type here to find address.'
                                                                handleChange={(place: any) => {
                                                                    if (place && place.properties) {
                                                                        const { formatted } = place.properties
                                                                        handleChange({
                                                                            target: {
                                                                                name: 'address',
                                                                                value: formatted,
                                                                            },
                                                                        })
                                                                    }
                                                                }}
                                                            />
                                                            <ErrorMessage name='address' component='div' className='text-danger mt-2' />
                                                        </div>
                                                    </div> */}
                          <div className='col-md-12'>
                            <div className='form-group margin-bottom-7'>
                              <label className='accept-invite-form-label'>Phone </label>
                              <PhoneInput
                                inputClass='w-100 accept-invite-form-value'
                                buttonClass='flag_dropdown'
                                dropdownStyle={{ margin: '-250px 0 0', borderRadius: '6px' }}
                                enableSearch={true}
                                disableSearchIcon={true}
                                searchStyle={{ width: '90%', margin: '0' }}
                                countryCodeEditable={false}
                                country='gr'
                                value={values.country_code + values.phone}
                                disabled={
                                  showOTPField ||
                                  (userData &&
                                    userData.users_groups?.some(
                                      (item: any) => item.is_invite_accepted === true
                                    ))
                                }
                                inputProps={{
                                  name: 'phone',
                                  id: 'phone',
                                  required: true,
                                }}
                                onChange={(phone: any, data: any) => {
                                  handleChange({
                                    target: {
                                      name: 'country_code',
                                      value: data.dialCode,
                                    },
                                  })
                                  let phone_number = phone.slice(data.dialCode.length)
                                  handleChange({
                                    target: {
                                      name: 'phone',
                                      value: phone_number,
                                    },
                                  })
                                }}
                                onBlur={() => {
                                  handleBlur({ target: { name: 'phone' } })
                                }}
                              />
                              <ErrorMessage
                                name='phone'
                                component='div'
                                className='text-danger mt-2'
                              />
                            </div>
                          </div>
                          {showOTPField && (
                            <div className='col-md-12'>
                              <div className='d-flex flex-column gap-4'>
                                <div className='otp-label'>Enter OTP to verify</div>
                                <CustomOtpInput
                                  label='otp'
                                  otp={otp}
                                  error={hasError}
                                  handleOTPChange={handleOTPChange}
                                  className='otp_input accept-invite-form-value'
                                  separatorClassName='separator'
                                />
                                {remainingTime !== 0 ? (
                                  <div className={`resend-text`}>
                                    Resend Code in{' '}
                                    <span className='seconds'>
                                      00:{formatSecondTime(remainingTime)}
                                    </span>
                                  </div>
                                ) : (
                                  <button
                                    type='button'
                                    className={`btn btn-link p-0 d-flex resend-btn`}
                                    onClick={() => {
                                      setResendOTPbuttonClicked(true)
                                      onClickResendOTP(values)
                                    }}
                                  >
                                    <u>Resend Code</u>
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                          <div className='col-md-12'>
                            <button
                              type='submit'
                              className={`btn btn-primary btn-pill w-100 mt-5 submit-btn ${actionsLoading ||
                                  (showOTPField && otp.length < 6) ||
                                  !isPasswordValid
                                  ? 'disabled-btn'
                                  : ''
                                }`}
                              disabled={
                                actionsLoading ||
                                (showOTPField && otp.length < 6) ||
                                !isPasswordValid
                              }
                            >
                              {showOTPField ? 'Submit' : 'Generate OTP'}
                              <img
                                className='ms-3'
                                src={'/media/icons/duotune/general/Arrow - Right.svg'}
                                alt='->'
                              />
                            </button>
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
                <div className='d-flex flex-column justify-content-center align-items-center w-100 flex-grow-1 flex-md-grow-0'>
                  <AuthFooter />
                </div>
              </div>
            </div>
          </div>
          <div className='col-md-5 p-0 background_card overflow-hidden position-relative'>
            <AuthSidePanel />
          </div>
        </div>
      )}
      {showConfirmationDialog && (
        <AcceptInviteDialog
          groups={values.groups}
          showConfirmationDialog={showConfirmationDialog}
          isFirstStepCompleted={false}
        />
      )}
    </>
  )
}
